/**
 * Saved growth playbook at `/admin/growth/playbook`.
 *
 * Static reference doc — the strategic advice the founder asked to
 * "save and put on the admin console so I can find it again." Lives
 * one click away from the live `/admin/growth` dashboard.
 *
 * Structured for re-reading: a TOC at the top, each tactic in its
 * own section with a copy-pasteable example where possible.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AppShell from "@/components/AppShell";
import { BookOpenIcon as BookOpen, TargetIcon as Target, MegaphoneIcon as Megaphone, UsersIcon as Users, CompassIcon as Compass, LightningIcon as Lightning, ChartLineUpIcon as ChartLineUp, HandHeartIcon as HandHeart, EnvelopeIcon as Envelope, ChartBarIcon as ChartBar, ArrowLeftIcon as ArrowLeft } from "@phosphor-icons/react";

const TOC = [
  { id: "diagnosis", label: "Honest diagnosis" },
  { id: "unlock", label: "The single biggest unlock — pick one persona" },
  { id: "outreach", label: "1. Direct outreach > broadcast" },
  { id: "assets", label: "2. Activate the assets already built" },
  { id: "youtube", label: "3. YouTube compounds — FB/IG don't" },
  { id: "clients", label: "4. Convert your clientele into a salesforce" },
  { id: "partnerships", label: "5. Partnerships > posts" },
  { id: "leads", label: "6. Lead-magnet email capture" },
  { id: "paid", label: "7. $5/day micro-paid tests" },
  { id: "metrics", label: "The 5 numbers to track weekly" },
  { id: "schedule", label: "Week-by-week schedule" },
  { id: "utm", label: "Appendix: UTM-tag every link" },
];

export default function AdminGrowthPlaybook() {
  return (
    <AppShell>
      <Helmet>
        <title>{"Growth playbook — Armstrong K9 Registry admin"}</title>
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-8 font-manrope text-ink">
        <div className="mb-2">
          <Link to="/admin/growth" className="inline-flex items-center gap-1 text-xs text-[#666] hover:text-ink" data-testid="playbook-back">
            <ArrowLeft size={12} /> Back to dashboard
          </Link>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold flex items-center gap-1.5">
          <BookOpen size={12} weight="duotone" /> Saved playbook
        </p>
        <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight mt-1">Growth playbook</h1>
        <p className="text-sm text-[#3F3F3F] mt-3 max-w-2xl leading-relaxed">
          The opinionated, ranked-by-leverage plan for getting Armstrong K9 Registry from "posting a few times a week" to hitting the conservative Year-1 user targets. Last updated Jun 6, 2026.
        </p>

        <nav className="mt-6 bg-white border border-[#EAEAEA] rounded-2xl p-4" data-testid="playbook-toc">
          <p className="text-xs uppercase tracking-wider text-[#888] mb-2">In this playbook</p>
          <ol className="text-sm text-[#3F3F3F] space-y-1 list-decimal pl-5">
            {TOC.map((t) => (
              <li key={t.id}>
                <a href={`#${t.id}`} className="hover:text-ink underline-offset-2 hover:underline">{t.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <Section id="diagnosis" icon={Compass} title="Honest diagnosis">
          <p>You're doing what 95% of marketplace founders do — generic broadcast content across 4 channels, hoping awareness converts. It almost never does. The 5% that grow do something specific.</p>
          <p>The platform itself is ready. The acquisition motion is the gap.</p>
        </Section>

        <Section id="unlock" icon={Target} title="The single biggest unlock — pick ONE persona">
          <p>You're trying to acquire 6+ personas simultaneously (breeders, buyers, owners, trainers, groomers, walkers, sitters, storefronts). Marketplaces always die from this.</p>
          <p><strong>Pick the persona where you have unfair advantage — trainers.</strong> You ARE a trainer. You speak the language. You have a client list. Trainer Learning is a real differentiator no competing directory has.</p>
          <ul>
            <li>60 active publishing trainers = $143K Y1 conservative ARR (per the business plan §2.14)</li>
            <li>Once trainers are listed, owners FIND the platform looking for them and convert naturally</li>
            <li>Two-sided marketplaces never start two-sided</li>
          </ul>
          <p><strong>Decision: ignore the other 5 personas for 90 days.</strong></p>
        </Section>

        <Section id="outreach" icon={Lightning} title="1. Direct outreach > broadcast posting">
          <p>Identify 200 dog trainers within 100 miles via Google/Yelp/IG/Reddit. DM 10/day with a personal message. Template:</p>
          <Code>
{`Hey [Name],

I noticed your post about [specific thing they posted about].

I'm building Armstrong K9 Registry — I'm a trainer myself. We just launched paid live video sessions + course uploads with Stripe Connect payouts. No subscription, 80% revenue share, you keep your own clients.

Want a 5-min walkthrough? — Logan`}
          </Code>
          <p><strong>Math:</strong> 200 DMs → 30 replies → 10 walkthroughs → 4 signups. Monthly. That's <strong>48 trainers in 12 months from 200 DMs/month</strong> — almost free.</p>
          <p>Compare to <em>one</em> Instagram post that gets 1,200 views and converts zero trainers.</p>
        </Section>

        <Section id="assets" icon={ChartLineUp} title="2. Activate the assets already paid for">
          <p>These shipped in the last 2 weeks and are sitting idle:</p>
          <ul>
            <li><strong><Link to="/registration" className="underline">/registration</Link> page</strong> — meta tags + OG image + schema ready. Run a $5/day Meta ad to dog-owner lookalikes pointing here. Should convert at 2-4%.</li>
            <li><strong>One-pager PDFs</strong> — attach <code>service_provider.pdf</code> to <em>every</em> trainer outreach DM. Attach <code>registration.pdf</code> as lead magnet for owners.</li>
            <li><strong>Schema.org/Course JSON-LD + sitemap.xml</strong> — submit to Google Search Console this week. Right now Google doesn't know your courses exist.</li>
            <li><strong>Completion certificates</strong> — every share = ~500 dog-owner LinkedIn impressions you never paid for.</li>
          </ul>
        </Section>

        <Section id="youtube" icon={Megaphone} title="3. YouTube compounds — FB/IG don't">
          <p>FB/IG posts die in 48 hours. YouTube videos live for years and rank in Google.</p>
          <p>Top-3 video topics by ROI:</p>
          <ol>
            <li><strong>"How to vet a dog trainer (10 red flags)"</strong> → drives traffic to <Link to="/find-pros" className="underline">/find-pros</Link></li>
            <li><strong>"Why I left [Rover/Petco/Yelp] and started accepting clients on Armstrong K9"</strong> → drives traffic to <Link to="/become-a-provider" className="underline">/become-a-provider</Link></li>
            <li><strong>"How I sold $X of online dog-training courses in 90 days"</strong> → trainer aspiration (record this once you HAVE numbers)</li>
          </ol>
          <p>Every video description: <code>Link in description → armstrongk9registry.com/{`{specific-page}`}?utm_source=youtube&utm_campaign={`{video-slug}`}</code>. Drive to a specific page, not the homepage.</p>
        </Section>

        <Section id="clients" icon={HandHeart} title="4. Convert your clientele into a salesforce">
          <p>Your existing "friends promo" is too soft. Try this instead:</p>
          <ul>
            <li>Give every existing client a personal QR-code referral link (live at <Link to="/me/referrals" className="underline">/me/referrals</Link>)</li>
            <li>Build a leaderboard: "Top referrers get a free training session every quarter"</li>
            <li>5 referrals = a free month of Care+ (or whatever your highest-margin sub is)</li>
            <li>Make it competitive, not transactional. Dog people are tribal.</li>
          </ul>
        </Section>

        <Section id="partnerships" icon={Users} title="5. Partnerships > posts">
          <p>ONE local vet clinic with 2,000 clients &gt; one IG post with 12,000 followers.</p>
          <p>The trade:</p>
          <ul>
            <li>Free "Armstrong K9 Registry — partner clinic" co-branded badge for their website</li>
            <li>They place a QR-coded postcard at front desk: "Register your dog free + get a DNA-verified badge"</li>
            <li>You feature them in a "Trusted local vet" widget on every dog profile in their zip</li>
          </ul>
          <p>Aim for <strong>5 partnership conversations/week</strong> with vets, groomers, boarding facilities, rescue orgs, breed-specific clubs. One signed = 50-200 registered dogs.</p>
        </Section>

        <Section id="leads" icon={Envelope} title="6. Lead-magnet email capture (now live)">
          <p>Cold ad traffic that doesn't convert immediately still gives you their email. The 7-day drip sequence is now wired up and dispatching automatically.</p>
          <p>The capture form is on <Link to="/registration" className="underline">/registration</Link> above the fold. Industry benchmark: 6-12% of email captures pay within 30 days.</p>
          <p>Track conversion on the <Link to="/admin/growth" className="underline">growth dashboard</Link> (lead → paid widget).</p>
        </Section>

        <Section id="paid" icon={ChartBar} title="7. $5/day micro-paid tests">
          <p>You don't need a marketing budget. You need a <strong>test budget</strong>. $5/day Meta ads:</p>
          <ul>
            <li><strong>Test 1:</strong> dog-owner lookalike → <code>/registration?utm_source=meta&utm_campaign=dna-badge</code></li>
            <li><strong>Test 2:</strong> trainer lookalike → <code>/become-a-provider?utm_source=meta&utm_campaign=trainer-recruit</code></li>
            <li><strong>Test 3:</strong> buyer lookalike → <code>/courses?utm_source=meta&utm_campaign=learn-from-vetted</code></li>
          </ul>
          <p>Kill the 2 that fail in 3 days. Scale the winner. At 3x ROAS, $150/mo pays for itself in 2 trainer signups.</p>
        </Section>

        <Section id="metrics" icon={ChartLineUp} title="The 5 numbers to track weekly">
          <ol>
            <li><strong>Cost per registered user (CAC)</strong></li>
            <li><strong>Registered → paid conversion %</strong> — how many $25 fees per week</li>
            <li><strong>Channel attribution</strong> — UTM tags on every external link</li>
            <li><strong>Time-to-second-touchpoint</strong> — how fast a signup does anything past auth</li>
            <li><strong>Trainer time-to-first-booking</strong> — most predictive metric on the platform</li>
          </ol>
          <p>All five live on <Link to="/admin/growth" className="underline">/admin/growth</Link>.</p>
        </Section>

        <Section id="schedule" icon={Target} title="Week-by-week schedule">
          <ul>
            <li><strong>Week 1:</strong> Submit sitemap.xml to Google Search Console. DM 50 trainers. Send the one-pager PDF to every existing client and ask them to forward to one person.</li>
            <li><strong>Week 2:</strong> Activate the referral mechanic for existing clients. Reach out to 5 local vet clinics for partnerships.</li>
            <li><strong>Week 3:</strong> Record 3 YouTube videos with specific titles + specific CTAs to specific pages.</li>
            <li><strong>Week 4:</strong> Launch $150/mo Meta ad tests, measure cost-per-registration.</li>
            <li><strong>Week 8:</strong> Look at the numbers. Double down on what's working. Cut what's not.</li>
          </ul>
        </Section>

        <Section id="utm" icon={Megaphone} title="Appendix: UTM-tag every link">
          <p>The growth dashboard's "Channel attribution" widget only fills in when your links are tagged. Three required UTM parameters:</p>
          <ul>
            <li><code>utm_source</code> = where it's posted (facebook, instagram, youtube, email, partner-vet-name)</li>
            <li><code>utm_medium</code> = post type (social, email, paid, qr)</li>
            <li><code>utm_campaign</code> = which campaign (registration-launch, trainer-recruit, march-newsletter)</li>
          </ul>
          <Code>{`https://armstrongk9registry.com/registration?utm_source=instagram&utm_medium=social&utm_campaign=march-launch`}</Code>
          <p>Pro tip: copy the table below into your IG bio link tool to keep them straight.</p>
          <div className="overflow-x-auto">
            <table className="text-xs mt-2 w-full border border-[#EAEAEA]">
              <thead className="bg-[#FAFAFA]"><tr>
                <th className="text-left px-2 py-1 border-b border-[#EAEAEA]">Channel</th>
                <th className="text-left px-2 py-1 border-b border-[#EAEAEA]">utm_source</th>
                <th className="text-left px-2 py-1 border-b border-[#EAEAEA]">utm_medium</th>
              </tr></thead>
              <tbody>
                {[
                  ["Facebook page", "facebook", "social"],
                  ["Instagram bio link", "instagram", "social"],
                  ["YouTube video description", "youtube", "social"],
                  ["Resend email blast", "email", "newsletter"],
                  ["Vet clinic QR postcard", "vet-{clinic-slug}", "qr"],
                  ["Meta paid ad", "meta", "paid"],
                  ["Direct trainer DM", "outbound", "dm"],
                ].map((r) => (
                  <tr key={r[0]}>
                    <td className="px-2 py-1 border-b border-[#EFEFEF]">{r[0]}</td>
                    <td className="px-2 py-1 border-b border-[#EFEFEF]"><code>{r[1]}</code></td>
                    <td className="px-2 py-1 border-b border-[#EFEFEF]"><code>{r[2]}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <div className="mt-12 pt-6 border-t border-[#EAEAEA] flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-[#888]">Bookmark this page · revise quarterly · the dashboard at <Link to="/admin/growth" className="underline">/admin/growth</Link> is where the numbers live.</p>
          <Link to="/admin/growth" className="inline-flex items-center gap-1.5 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-4 py-2 text-sm font-semibold transition-colors">
            Open the dashboard <BookOpen size={13} />
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

function Section({ id, icon: I, title, children }) {
  return (
    <section id={id} className="mt-8 scroll-mt-20" data-testid={`playbook-section-${id}`}>
      <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
        <I size={18} weight="duotone" className="text-[#C1272D]" /> {title}
      </h2>
      <div className="prose prose-sm max-w-none mt-3 text-[#3F3F3F]
        prose-strong:text-ink
        prose-a:text-[#C1272D] prose-a:no-underline hover:prose-a:underline
        prose-li:my-1 prose-p:leading-relaxed
        prose-code:bg-[#FAFAFA] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[#0E0E0E] prose-code:before:content-none prose-code:after:content-none">
        {children}
      </div>
    </section>
  );
}

function Code({ children }) {
  return (
    <pre className="bg-[#0E0E0E] text-[#F5F5F5] font-mono text-[12px] leading-relaxed rounded-xl p-4 my-3 overflow-x-auto whitespace-pre-wrap">
      {children}
    </pre>
  );
}
